import { Button, Divider, Drawer, Form, Input, message, Space, Spin, Table, Tag, Timeline, Typography } from 'antd';
import Search from 'antd/lib/input/Search';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { getConfiguration, getWorkflowList, patchConfiguration } from '../../services/WorkflowService';
import { WorkflowJob } from '../../types/WorkflowJob';

type WorkflowTableProps = {};

const workflowListSearchTextFilter = (record: WorkflowJob, searchText: string): boolean => {
  if (searchText === '') return true;
  return record.appId.toLowerCase().includes(searchText);
};

export const WorkflowTable: FunctionComponent<WorkflowTableProps> = () => {
  const [searchText, setSearchText] = useState<string>('');
  const [workflowList, setWorkflowList] = useState<WorkflowJob[]>();
  const [taskDetailOpen, setTaskDetailOpen] = useState<boolean>(false);
  const [viewTask, setViewTask] = useState<WorkflowJob>();
  const [workflowConfigurationOpen, setWorkflowConfigurationOpen] = useState<boolean>(false);
  const [settingForm] = Form.useForm();

  useEffect(() => {
    reloadWorkflowList();
  }, []);

  const reloadWorkflowList = async () => {
    try {
      const workflowList = await getWorkflowList();
      setWorkflowList(workflowList);
    } catch (e) {
      setWorkflowList([]);
    }
  };

  const onSearch = (searchText: string) => {
    if (searchText) {
      setSearchText(searchText.toLowerCase());
    } else {
      setSearchText('');
    }
  };

  const columns = [
    { title: 'Task ID', dataIndex: 'id' },
    { title: 'Kapsule', dataIndex: 'kapsuleId' },
    {
      title: 'Tasks',
      render(record: WorkflowJob) {
        return record.tasks.map((task) => {
          return <Tag key={task.cId}>{task.taskName}</Tag>;
        });
      }
    },
    {
      title: 'Status', dataIndex: 'status',
      render(status: string) {
        if (status === 'RUNNING') {
          return <Tag color='processing'>RUNNING</Tag>;
        } else if (status === 'FAILED') {
          return <Tag color='error'>FAILED</Tag>;
        } else if (status === 'COMPLETED') {
          return <Tag color='success'>COMPLETED</Tag>;
        } else if (status === 'COMPLETED_WITH_ERROR') {
          return <Tag color='warning'>COMPLETED WITH ERROR</Tag>;
        }
      }
    },
    { title: 'LastFinishedAt', dataIndex: 'lastFinishedAt' },
    { title: 'Version', dataIndex: 'version' },
    {
      title: 'Details', render(record: WorkflowJob) {
        return <Button type='link' onClick={() => {
          setViewTask(record);
          setTaskDetailOpen(true);
        }}>view</Button>
      }
    }
  ];

  const loadConfiguration = async () => {
    try {
      const configuration = await getConfiguration();
      settingForm.setFieldsValue(configuration);
    } catch (e) {
      message.error('Error: Failed to load configuration!');
    }
  };

  const updateConfiguration = async (values: any) => {
    try {
      await patchConfiguration(values);
      message.success('Success: Configuration updated!');
      setWorkflowConfigurationOpen(false);
    } catch (e) {
      message.error('Error: Failed to update configuration!')
    }
  }

  return workflowList ? (
    <>
      <Table
        title={() => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Space>
              <b>Workflow Jobs</b>
              <Button type='link' onClick={() => setWorkflowConfigurationOpen(true)}>configure</Button>
            </Space>
            <Space>
              <Search placeholder="Search by App ID" allowClear onSearch={onSearch} style={{ width: 200 }} />
            </Space>
          </div>
        )}
        expandable={{
          expandedRowRender: (record) => {
            const text = JSON.stringify(record, undefined, 4);
            return (
              <pre>
                <code>{text}</code>
              </pre>
            );
          },
          rowExpandable: (record) => !!record,
        }}
        rowKey={(record) => record.id}
        columns={columns}
        dataSource={workflowList.filter((item) => workflowListSearchTextFilter(item, searchText))}
      />

      <Drawer
        title="Workflow Configuration"
        placement={'right'}
        open={workflowConfigurationOpen}
        width={600}
        afterOpenChange={(open) => {
          if (open) {
            loadConfiguration();
          }
        }}
        onClose={() => setWorkflowConfigurationOpen(false)}
      >
        <Form
          form={settingForm}
          layout='vertical'
          onFinish={updateConfiguration}
        >
          <Typography.Title level={5} style={{ marginTop: 0 }}> Medidata</Typography.Title>
          <Form.Item label="Tenant" name={['medidata', 'tenant']}>
            <Input />
          </Form.Item>
          <Form.Item label="Username" name={['medidata', 'username']}>
            <Input />
          </Form.Item>
          <Form.Item label="Password" name={['medidata', 'password']}>
            <Input.Password />
          </Form.Item>
          <Divider />

          <Button type='primary' htmlType='submit'>Save & Close</Button>
        </Form>
      </Drawer >

      <Drawer
        title="Task Details"
        placement={'right'}
        open={taskDetailOpen}
        width={600}
        onClose={() => setTaskDetailOpen(false)}
      >
        <Timeline
          mode='left'
          items={viewTask?.details.map((item) => {
            if (item.cId) {
              if (item.success) {
                return {
                  color: 'green',
                  label: item.recordedAt,
                  children: (
                    <>
                      <Tag color={'green'}>{item.taskName}</Tag>
                    </>
                  ),
                }
              } else {
                return {
                  color: 'red',
                  label: item.recordedAt,
                  children: (
                    <>
                      <Tag color={'red'}>{item.taskName}</Tag>
                      <p>{item.error}</p>
                    </>
                  ),
                }
              }
            } else {
              return {
                color: 'gray',
                label: item.recordedAt,
                children: item.message,
              }
            }
          }) ?? []}
        />
      </Drawer>
    </>
  ) : (
    <div style={{ height: 312, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Spin />
    </div>
  );
};
